import * as dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import decode from 'jwt-decode';
import storage from './Storage';

dayjs.extend(duration);

interface IUserTokenData {
  id: string;
  firstName: string;
  lastName: string;
  iat: number;
  exp: number;
}

interface ITokenData {
  id: string;
  first_name: string;
  last_name: string;
  iat: number;
  exp: number;
}

interface ITokenStatus {
  isPresent: boolean;
  isExpired?: boolean;
}

export const USER_TOKEN_NAME = process.env.REACT_APP_USER_TOKEN_NAME || 'jwt_token';

const AuthService = {
  tokenStatus: (): ITokenStatus => {
    const token = AuthService.getToken();
    if (!token) {
      return {
        isPresent: false,
      };
    }
    if (AuthService.isTokenExpired(token)) {
      return {
        isPresent: true,
        isExpired: true,
      };
    }
    return {
      isPresent: true,
      isExpired: false,
    };
  },
  getToken: (): string | undefined => {
    const token = storage.getItem(USER_TOKEN_NAME);
    if (token === null) return undefined;
    return token;
  },
  storeToken: (jwt: string): boolean => {
    storage.setItem(USER_TOKEN_NAME, jwt);
    return true;
  },
  clearToken: (): boolean => {
    storage.removeItem(USER_TOKEN_NAME);
    return true;
  },
  getActiveUser: (): IUserTokenData | undefined => {
    const jwt = AuthService.getToken();
    if (!jwt) return undefined;
    const token = decode<ITokenData>(jwt);
    if (token === null) return undefined;
    return {
      id: token.id,
      firstName: token.first_name,
      lastName: token.last_name,
      iat: token.iat,
      exp: token.exp,
    };
  },
  secondsUntilExpired: (tokenString: string): number => {
    try {
      const decodedToken = decode<ITokenData>(tokenString);
      const { exp } = decodedToken;
      if (exp) {
        const tokenExpirationDate = new Date(0);
        tokenExpirationDate.setUTCSeconds(exp);
        const currentDate = new Date().getTime();
        return (tokenExpirationDate.getTime() - currentDate) / 1000;
      }
      return -1;
    } catch {
      return -1;
    }
  },
  timeRemainingDisplay: (): string => {
    const token = AuthService.getToken();
    let timeRemaining;
    if (token) {
      timeRemaining = dayjs.duration(AuthService.secondsUntilExpired(token), 'seconds').humanize(true);
    } else {
      timeRemaining = dayjs.duration(0, 'seconds').humanize(true);
    }
    return timeRemaining;
  },
  isTokenExpired: (token: string): boolean => AuthService.secondsUntilExpired(token) <= 0,
};

export default AuthService;
